import ApiRequestVO from "../../../manager/api/ApiRequestVO";
import {IAnyObject} from "../../../common/SimpleInterface";
import ApiError from "../../../manager/api/ApiError";
import {FactoryID} from "../../../model-factory/type-interface/ModelFactoryType";
import {IPopulateIDs} from "../../../model-factory/type-interface/ModelFactoryInterface";
import {getModelFactory} from "../../../model-factory/ModelFactory";
import {SearchBasicDataModel, SearchParams} from "../../search/SearchBasicDataFactory";
import DataFactory from "../../../model-factory/basic/data/DataFactory";
import DataChoice, {IDataChoices} from "../../../model-factory/basic/data/DataChoice";
import {MessagePageListDataModel} from "./MessagePageListDataFactory";
import UserProfile from "../../../manager/user/UserProfile";
import {isNativeObject, isNumeric, isString, unixToDate} from "../../../common/SiteHelper";
import {getManager} from "../../../manager/Manager";


export type MessageDataSortBy =
    "date";

class MessageActionVO
{
    actionID:string;
    text:string;

    constructor(object:IAnyObject)
    {
        this.actionID = object.action_id ?? "";
        this.text = object.text ?? this.actionID;
    }
}

class MessageSourceInfoVO
{
    organisationID:number;

    constructor(object:IAnyObject)
    {
        this.organisationID = parseInt(object.organisation_id ?? 0);
    }
}

export class MessageDataChoice extends DataChoice
{
    messageID:number;
    title:string;
    body:string;
    fromUser:UserProfile;
    isAdminGenerated:boolean;
    sourceInfo:MessageSourceInfoVO;
    actions:MessageActionVO[] = [];
    isUnread:boolean;
    isHidden:boolean;
    isExpired:boolean;
    canNotDelete:boolean;
    created:Date|null = null;
    updated:Date|null = null;

    constructor(object:IAnyObject)
    {
        object.name = object.name ?? object.title;
        super(object);

        this.messageID = isNumeric(this.id) ? parseInt(this.id) : 0;
        this.title = object.title ?? this.id;
        this.body = object.body ?? "";

        this.fromUser = new UserProfile()
        if(isNativeObject(object.from_user)){
            this.fromUser.userID = isNumeric(object.from_user.id) ? parseInt(object.from_user.id) : 0;
            this.fromUser.firstName = isString(object.from_user.first_name) ? object.from_user.first_name : "";
            this.fromUser.lastName = isString(object.from_user.last_name) ? object.from_user.last_name : "";
        }
        if(!this.fromUser.userID && !this.fromUser.firstName && !this.fromUser.lastName){
            this.fromUser.firstName = getManager().app.site.title;
        }
        this.isAdminGenerated = !!object.is_admin_generated;
        const fromSourceInfo = object.source_info;
        this.sourceInfo = new MessageSourceInfoVO(isNativeObject(fromSourceInfo) ? fromSourceInfo : {});
        const fromActions = object.actions;
        if(Array.isArray(fromActions)){
            for(let fromAction of fromActions){
                if(isNativeObject(fromAction)){
                    const action = new MessageActionVO(fromAction);
                    if(action.actionID){
                        this.actions.push(action);
                    }
                }
            }
        }
        this.isUnread = !!object.is_unread;
        this.isHidden = !!object.is_hidden;
        this.isExpired = !!object.is_expired;
        this.canNotDelete = !!object.can_not_delete;
        this.created = unixToDate(object.created);
        this.updated = unixToDate(object.updated);
    }
}

export interface IMessageDataChoices extends IDataChoices
{
    [key: string]: MessageDataChoice;
}

export class MessageSearchParams extends SearchParams
{
    sortBy:MessageDataSortBy = "date";
    sortDesc:boolean = true;
}

export class MessageDataModel extends SearchBasicDataModel
{

    searchParams:MessageSearchParams = new MessageSearchParams();

    choices:IMessageDataChoices = {};

    messageID:number = 0;

    isEntryModel:boolean = false;
    isSharedData:boolean = false;
    markAsReadOnPopulate:boolean = false;

    applyModelConditions()
    {
        super.applyModelConditions();

        this.pageNumber = Math.max(this.populateIDs.page_number ?? 0);

        this.messageID = this.populateIDs.message_id ?? 0;
        this.isEntryModel = !!this.messageID;
        this.isSharedData = this.populateIDs.is_shared_data ?? false;
        this.populated = this.isSharedData;
        this.markAsReadOnPopulate = this.isEntryModel;
    }

    getPopulateRequestVO(): ApiRequestVO|null
    {
        if(this.isSharedData ){
            return null;
        }
        const requestVO = new ApiRequestVO(true);
        if(this.messageID){
            requestVO.endpoint = "/message/"+this.messageID+"/entry";
            requestVO.params = {
                mark_as_read: this.markAsReadOnPopulate
            };
        } else {

            requestVO.endpoint = "/message/list";
            requestVO.params = {
                return_basic: true
            };
            if(this.searchParams.searchTermID && this.searchParams.searchTermID !== "search_all"){
                requestVO.params.search_term = encodeURIComponent(this.searchParams.searchTerm);
            }
            if(!this.searchParams.sortDesc){
                requestVO.params.sort_asc = true;
            }

            this.addPageParamsToRequest(requestVO);

        }

        return requestVO;
    }

    getMessage():MessageDataChoice|null
    {
        return this.messageID ? this.choices[this.messageID] : null;
    }

    choiceList(choiceOrder?:string[]): MessageDataChoice[]
    {
        return super.choiceList(choiceOrder) as MessageDataChoice[];
    }

    populateRelatedModels(response:IAnyObject)
    {
        super.populateRelatedModels(response);
        const choiceList = this.choiceList();
        if(this.messageID){
            this.factory().getSharedDataModel().addToChoiceList(choiceList);
            getModelFactory().messagePageListData.replaceSelectedPageChoiceList(choiceList);
        }
    }

    populateWithRequestResponse(response: IAnyObject, replaceData:boolean): ApiError | null
    {
        let error =  super.populateWithRequestResponse(response, replaceData);
        if(error){
            return error;
        }
        const objectArray:IAnyObject[] = !this.messageID ? (response.items ?? []) : (response.item ? [response.item] : []);
        const unreadMessageCount= parseInt(response.unread_message_count ?? "-1");
        if(unreadMessageCount >= 0){
            getManager().user.unreadMessageCount = unreadMessageCount;
        }

        this.populateChoiceList(objectArray, replaceData);
        this.applyResponseToRelatedPages(response);

        return null;
    }

    getPageModel(): MessagePageListDataModel|null
    {
        if(!this.pageNumber){
            return null;
        }
        return getModelFactory().messagePageListData.getMessagePageListDataModel(this.searchParams );
    }

    resetPopulated()
    {
        super.resetPopulated();
        if(this.isSharedData){
            this.populated = true;
        }
    }

    factory(): MessageDataFactory
    {
        return super.factory() as MessageDataFactory;
    }
}

export default class MessageDataFactory extends DataFactory
{

    id: FactoryID = "message_data";

    birth(id: string): MessageDataModel
    {
        return new MessageDataModel(id);
    }

    birthDataChoice(object:IAnyObject): MessageDataChoice
    {
        return new MessageDataChoice(object);
    }

    getMessageDataModel(searchParams:MessageSearchParams|null = null, pageNumber:number = 1): MessageDataModel
    {
        searchParams = searchParams ? searchParams : new MessageSearchParams();
        const populateIDs:IPopulateIDs = {
            search_sort_by: searchParams.sortBy,
        };
        if(searchParams.searchTermID){
            populateIDs.search_term_id = searchParams.searchTermID;
        }
        if(pageNumber){
            populateIDs.page_number = pageNumber;
        }
        return this.requireModel(populateIDs) as MessageDataModel;
    }

    getSharedDataModel(): MessageDataModel
    {
        const populateIDs:IPopulateIDs = {
            is_shared_data: true
        }
        return this.requireModel(populateIDs) as MessageDataModel
    }

    getEntryDataModel(messageID:number): MessageDataModel
    {
        const populateIDs:IPopulateIDs = {
            message_id: messageID
        }
        return this.requireModel(populateIDs) as MessageDataModel
    }

    resetOrganisationMessagesPopulated(organisationID:number): void
    {
        const messageIDs:number[] = [];
        const messages = this.getSharedDataModel().choiceList();
        for(let message of messages){
            if(message.sourceInfo.organisationID === organisationID){
                messageIDs.push(message.messageID);
            }
        }
        if(!messageIDs.length){
            return;
        }

        for(let modelID in this.conditionModels)
        {
            const model = this.conditionModels[modelID] as MessageDataModel;
            if(messageIDs.includes(model.messageID)){
                model.resetPopulated();
            }
        }
    }
}